import { gsap } from "gsap";
import { IDestroyOptions, Text, TextStyle } from "pixi.js";
import { DynamicBackground } from "../../components/DynamicBackground";
import { PromiseCompletionSource } from "async/PromiseCompletionSource";
import { ICanvasScaler } from "by/gamefactory/canvas/ICanvasScaler";
import { GameFont } from "by/gamefactory/cityversetycoon/scenes/common/GameFont";
import { BasePageView } from "by/gamefactory/cityversetycoon/scenes/common/components/BasePageView";
import { ButtonWithScaleAndPosYAnimation } from "by/gamefactory/cityversetycoon/scenes/common/components/ButtonWithScaleAndPosYAnimation";
import { CommonResourcesPackage } from "by/gamefactory/cityversetycoon/scenes/common/resources/CommonResourcesPackage";
import { FadeScreen } from "by/gamefactory/cityversetycoon/scenes/game/components/FadeScreen";
import { StarsResourcePackage } from "by/gamefactory/cityversetycoon/scenes/common/resources/StarsResourcePackage";

export class CompletionPage extends BasePageView {
    private readonly commonResources: CommonResourcesPackage;
    private readonly starsResources: StarsResourcePackage;
    private readonly operationControls: PromiseCompletionSource<void>;
    private fadeScreen: FadeScreen;
    private headerText: Text;
    private additionalText: Text;
    private marketplaceText: Text;
    private button: ButtonWithScaleAndPosYAnimation;
    private starsBackground: DynamicBackground;

    public constructor(
        operationControls: PromiseCompletionSource<void>,
        canvasScaler: ICanvasScaler,
        commonResources: CommonResourcesPackage,
        starsResources: StarsResourcePackage
    ) {
        super(canvasScaler);
        this.commonResources = commonResources;
        this.starsResources = starsResources;
        this.operationControls = operationControls;
        this.build();
        this.once("added", this.show.bind(this));
    }

    protected override build(): void {
        this.fadeScreen = new FadeScreen(0.1, 0.4, 0.8, this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.fadeScreen.eventMode = "static";
        this.addChild(this.fadeScreen);
        this.starsBackground = new DynamicBackground(this.starsResources, this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.addChild(this.starsBackground);
        this.headerText = new Text('YOUR BUILDING\nWAS REMOVED', new HeaderTextStyle());
        this.headerText.anchor.set(0.5);
        this.addChild(this.headerText);
        this.additionalText = new Text('FROM', new HeaderTextStyle());
        this.additionalText.anchor.set(0.5);
        this.addChild(this.additionalText);
        this.marketplaceText = new Text('MARKETPLACE', new MarketplaceTextStyle());
        this.marketplaceText.anchor.set(0.5);
        this.addChild(this.marketplaceText);
        this.button = new ButtonWithScaleAndPosYAnimation(this.commonResources.assets_common_ui_button_green_png);
        this.button.onUp.add(this.onRemove, this);
        this.addChild(this.button);
        const buttonText = new Text('THANKS', new ButtonTextStyle());
        buttonText.position.set(88, 48);
        this.button.defaultView.addChild(buttonText);
        super.build();
    }

    private async show(): Promise<void> {
        this.fadeScreen.fadeIn();
        this.starsBackground.playIdleAnimation();
    }

    protected override layout(width: number, height: number): void {
        this.starsBackground.position.set(width * 0.5, height * 0.5);
        this.headerText.position.set(width * 0.5, 827.64);
        this.additionalText.position.set(270, 944.64);
        this.marketplaceText.position.set(474.58, 944.64);
        this.button.position.set(228, 1101);
        super.layout(width, height);
    }

    private async onRemove(): Promise<void> {
        this.button.interactive = false;
        const animationTimeLine = new gsap.core.Timeline();
        animationTimeLine.delay(0.2);
        await animationTimeLine;
        animationTimeLine.kill();
        this.operationControls.complete();
    }

    public destroy(options?: boolean | IDestroyOptions): void {
        this.button.onUp.remove(this.onRemove, this);
        super.destroy(options);
    }
}

class MarketplaceTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            align: "center",
            fill: "#37d2c9",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 64
        }));
    }
}

class HeaderTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            align: "center",
            fill: "#ffffff",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 64,
            lineHeight: 71.5
        }));
    }
}

class ButtonTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            dropShadow: true,
            dropShadowAngle: 0.1,
            dropShadowBlur: 6,
            dropShadowDistance: 2,
            fill: "#ffffff",
            align: "center",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 78
        }));
    }
}